import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { QRCode } from 'react-qr-code'
import { X, Users, Crosshair, Shield, Download } from 'lucide-react'
import { downloadQRPng } from '../lib/printQR'

const C = {
  display: { fontFamily: "'VT323', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
}

const JOIN_TYPES = [
  { type: 'event', label: 'EVENT JOIN', icon: Crosshair, description: 'Scanner joins unassigned. They pick their own faction.', color: '#ffcc33', bg: 'rgba(255,204,51,.08)', border: 'rgba(255,204,51,.3)' },
  { type: 'event_squad', label: 'SQUAD JOIN', icon: Shield, description: 'Scanner joins your faction automatically.', color: '#4da0ff', bg: 'rgba(77,160,255,.08)', border: 'rgba(77,160,255,.3)' },
  { type: 'team', label: 'TEAM JOIN', icon: Users, description: 'Scanner gets added to your team.', color: '#00ff66', bg: 'rgba(0,255,102,.08)', border: 'rgba(0,255,102,.3)' },
]

export default function ShareQR({ event, factionId, teamId, onClose }) {
  const { user } = useAuth()
  const [selectedType, setSelectedType] = useState(event ? 'event' : 'team')
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const availableTypes = JOIN_TYPES.filter(t => {
    if (t.type === 'event' || t.type === 'event_squad') return !!event
    if (t.type === 'team') return !!teamId
    return false
  })

  useEffect(() => { if (availableTypes.length > 0) setSelectedType(availableTypes[0].type) }, [])
  useEffect(() => { generateToken() }, [selectedType])

  const generateToken = async () => {
    if (!user) return; setLoading(true); setError(''); setToken(null)
    const payload = { created_by: user.id, join_type: selectedType, event_id: (selectedType === 'event' || selectedType === 'event_squad') ? event?.id : null, faction_id: selectedType === 'event_squad' ? factionId : null, team_id: selectedType === 'team' ? teamId : null, expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() }
    const { data, error: err } = await supabase.from('join_tokens').insert(payload).select().limit(1)
    if (err) { setError('Failed: ' + err.message) } else { setToken(data[0]) }
    setLoading(false)
  }

  const joinUrl = token ? `${window.location.origin}/join/${token.token}` : null

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
      <div style={{ background: '#0a140a', border: '1px solid rgba(0,255,102,.3)', boxShadow: '0 0 20px rgba(0,255,102,.15)', width: '100%', maxWidth: 420, position: 'relative', color: '#00ff66', fontFamily: "'JetBrains Mono', monospace", textShadow: '0 0 4px rgba(0,255,102,.5)' }}>
        <div style={{ position:'absolute',top:-1,left:-1,width:10,height:10,borderTop:'2px solid #9aff9a',borderLeft:'2px solid #9aff9a' }}/>
        <div style={{ position:'absolute',bottom:-1,right:-1,width:10,height:10,borderBottom:'2px solid #9aff9a',borderRight:'2px solid #9aff9a' }}/>

        <div className="flex items-center justify-between" style={{ padding: '16px 20px', borderBottom: '1px solid rgba(0,255,102,.18)' }}>
          <h2 style={{ ...C.display, fontSize: '18px', margin: 0 }}>SHARE QR CODE</h2>
          {/* Track 6.2 — 44x44 min hit area on the modal close. */}
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer', minHeight: 44, minWidth: 44, padding: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X size={20} /></button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
            {availableTypes.map(t => {
              const Icon = t.icon
              const isSel = selectedType === t.type
              return (
                <button key={t.type} onClick={() => setSelectedType(t.type)}
                  style={{ width: '100%', textAlign: 'left', padding: 12, cursor: 'pointer', background: isSel ? t.bg : 'rgba(0,20,8,.3)', border: isSel ? `2px solid ${t.border}` : '2px solid rgba(0,255,102,.08)', color: isSel ? t.color : '#4aaa5a', textShadow: isSel ? `0 0 4px ${t.color}50` : 'none', fontFamily: "'JetBrains Mono', monospace" }}>
                  <div className="flex items-center gap-3">
                    <Icon size={16} />
                    <div>
                      <p style={{ fontWeight: 500, fontSize: 13, ...C.display }}>{t.label}</p>
                      <p style={{ fontSize: 10, marginTop: 2, opacity: 0.7 }}>{t.description}</p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          <div className="flex flex-col items-center">
            {loading ? (
              <div style={{ width: 192, height: 192, background: 'rgba(0,20,8,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <p style={{ color: '#4aaa5a', fontSize: 12 }}>GENERATING...</p>
              </div>
            ) : error ? (
              <div style={{ width: '100%', padding: 16, background: 'rgba(255,42,42,.08)', border: '1px solid rgba(255,42,42,.2)', color: '#ff2a2a', fontSize: 12, textAlign: 'center' }}>{error}</div>
            ) : token ? (
              <>
                <div style={{ background: '#fff', padding: 16 }}><QRCode id={`share-qr-${token.token}`} value={joinUrl} size={180} /></div>
                <p style={{ color: '#2a6a3a', fontSize: 10, marginTop: 12, textAlign: 'center' }}>Expires in 24 hours · Single use</p>
                <p style={{ color: '#2a6a3a', fontSize: 10, marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>{token.token}</p>
              </>
            ) : null}
          </div>

          {token && (
            <div className="flex gap-2" style={{ marginTop: 16 }}>
              <button onClick={() => downloadQRPng({ svgElementId: `share-qr-${token.token}`, filename: `dc-${selectedType}-${token.token.slice(0, 8)}` })}
                className="flex items-center justify-center gap-2"
                style={{ flex: 1, padding: '8px 14px', fontSize: 12, color: '#9aff9a', background: 'rgba(0,255,102,.08)', border: '1px solid rgba(0,255,102,.3)', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", textShadow: '0 0 4px rgba(0,255,102,.3)' }}>
                <Download size={12} /> SAVE PNG
              </button>
              <button onClick={generateToken} style={{ flex: 1, padding: '8px 14px', fontSize: 12, color: '#4aaa5a', background: 'none', border: '1px solid rgba(0,255,102,.18)', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", textShadow: '0 0 4px rgba(0,255,102,.3)' }}>
                REGENERATE QR
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
